import { DrugConfirmation, Prescription } from "@fusion-advantage/backcompat"
import { medReconItemCanAddIndication, medReconItemCanSelectReason, MedReconItemNotInUseReason } from "@fusion-advantage/formlib"
import { V2L1SmallRoundIcon, V2L1SmallRoundIconSize } from "../../uiv2/Indicator/V2L1SmallRoundIcon"
import { faCamera, faCheck, faCircleExclamation, faKeyboard, faPlus, faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons'
import styled, { CSSObject } from "@emotion/styled"
import { V2L1BooleanButton } from "../../uiv2/Button/V2L1BooleanButton"
import { FusionColor } from "../../../theme/FusionStyle"
import { L1ReasonableButton } from "./L1Button"
import { ReactNode, useCallback, useMemo } from "react"
import L1Select from "./L1Select"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClock } from "@fortawesome/pro-regular-svg-icons"
import { L2Dialog, L2DialogActions, L2DialogContent } from "../l2/L2Dialog"

type L1MedicationReconcilliationTableRowProps = {
	isReconciled?: boolean
	showDivider?: boolean
}

export const L1MedicationReconcilliationTableRow = styled.div<L1MedicationReconcilliationTableRowProps>(({theme, isReconciled, showDivider}) => ({
	backgroundColor: isReconciled ? theme.colors.second : undefined,
	gridColumn: 'rowstartln / rowendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	breakInside: 'avoid',
	borderBottom: showDivider ? `1px solid ${theme.colors.lines}` : undefined,
	position: 'relative',
	mixBlendMode: 'multiply',
}))

const L1MedicationReconcilliationTableSourceRoot = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	position: 'relative'
})

const SourceSecondaryIconRoot = styled.div({
	position: 'absolute',
	left: -10,
	top: 21,
})

const L1MedicationReconcilliationTableInUseRoot = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	gap: theme.spacing(1.5),
}))

const L1MedicationReconcilliationTableIndicationsRoot = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'flex-start',
	fontSize: 16,
	// due to variations in rendering in Figma vs browser
	// changing this one text label to normal instead of 600 --cblack
	// fontWeight: 600,
	width: '100%',
})

const L1MedicationReconcilliationTableIndication = styled.div({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	width: '100%',
})

export const L1MedicationReconcilliationTableName = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	fontSize: 16,
	fontWeight: 600,
	justifyContent: 'center',
	alignItems: 'flex-start',
	flexWrap: 'wrap',
	overflowWrap: 'anywhere',
	width: '100%'
}))

export const L1MedicationReconcilliationTableReasonRoot = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	width: '100%',
}))

export const ScanOrManuallyEnterWarningRoot = styled.div(({theme}): CSSObject => ({
	fontSize: 16,
	fontWeight: 600,
	fontStyle: 'italic',
	color: theme.colors.bloodOrange,
	gridColumn: 'span 3',
	alignSelf: 'center',
})) 

export const SelectedResultRow = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
}))

export const SuggestedResult = styled.div(({theme}) => ({
	color: theme.colors.blueMain,
	fontStyle: 'italic',
	display: 'inline-block',
}))

type PlaceholderColumnProps = {
	reconciled: boolean
}

export const ActionsColumn = styled.div<PlaceholderColumnProps>(({theme, reconciled}) => ({
	backgroundColor: reconciled ? theme.colors.third : theme.colors.second,
	mixBlendMode: 'multiply',
	width: '100%',
	height: '100%',
	padding: theme.spacing(1),
	overflow: 'hidden',
	boxSizing: 'border-box',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	outline: `1px solid ${theme.colors.second}`,
	color: theme.colors.blueMain,
}))

export type ActionRequiredRowIndicatorProps = {
	satisfied?: boolean
}

export const ActionRequiredRowIndicator = styled.div<ActionRequiredRowIndicatorProps>(({theme, satisfied}) => ({
	backgroundColor: satisfied ? theme.colors.transparent : theme.colors.orangeMain,
	mixBlendMode: 'multiply',
	width: '100%',
	height: '100%',
}))

export const MedReconDialogContentContainer = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
	width: '100%',
	background: theme.colors.second,
	padding: theme.spacing(2),
	boxSizing: 'border-box',
	overflow: 'auto',
	height: '600px',
}))

type CollapsedContentContainerProps = {
	backgroundColor?: FusionColor,
	color?: FusionColor,
}

export const CollapsedContentContainer = styled.div<CollapsedContentContainerProps>(({theme, color, backgroundColor = FusionColor.Third}) => ({
	width: '100%',
	padding: theme.spacing(2),
	background: theme.colors[backgroundColor],
	borderRadius: theme.borderRadius,
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
	boxSizing: 'border-box',
	color,
}))

export const ScrollShadowWrapper = styled.div(({theme}) => ({
	background: `
		linear-gradient(#fff 33%, rgba(82, 52, 52, 0)),
		linear-gradient(rgba(255,255,255, 0), #fff 66%) 0 100%,
		linear-gradient(rgba(50,50,50, 0.2), rgba(0,0,0,0)),
		linear-gradient(rgba(0,0,0,0), rgba(50,50,50, 0.2)) 0 100%;
	`,
	backgroundColor: '#fff',
	backgroundRepeat: 'no-repeat',
	backgroundAttachment: 'local, local, scroll, scroll',
	backgroundSize: '100% 45px, 100% 45px, 100% 10px, 100% 10px',
	overflow: 'auto'
}))

export const SearchResultsContainer = styled.div(({theme}) => ({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
	borderRadius: theme.borderRadius,
	position: 'relative',
}))

type SearchResultsListProps = {
	maxHeight?: number
}

export const SearchResultsList = styled.div<SearchResultsListProps>(({theme, maxHeight}): CSSObject => ({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	border: `1px solid ${theme.colors.lines}`,
	borderRadius: theme.borderRadius,
	maxHeight,
	overflowY: 'auto',
}))

type SearchResultRowProps = {
	showDivider: boolean,
}

export const SearchResultRow = styled.div<SearchResultRowProps>(({theme, showDivider}) => ({
	width: '100%',
	display: 'flex',
	flexDirection: 'row',
	boxSizing: 'border-box',
	background: theme.colors.white,
	borderTop: showDivider ? `1px solid ${theme.colors.lines}` : undefined,
}))

export const SearchResultRowText = styled.div(({theme}) => ({
	display: 'flex',
	flex: 9,
	justifySelf: 'flex-start',
	flexDirection: 'row',
	alignSelf: 'center',
	flexWrap: 'wrap',
	padding: theme.spacing(1.5, 2),
	gap: theme.spacing(0.5),
}))

export const SearchResultRowActions = styled.div(({theme}) => ({
	justifySelf: 'flex-end',
	alignSelf: 'center',
	padding: theme.spacing(1.5, 2),
}))

interface L1MedicationReconcilliationTableSourceProps {
	model: Prescription,
	reconciled: boolean,
}

interface L1MedicationReconcilliationTableInUseProps {
	model: Prescription,
	reconciled: boolean,
	onChange: (m: Prescription) => void,
}

interface L1MedicationReconcilliationTableIndicationsProps {
	model: Prescription,
	reconciled: boolean,
	onAddIndication: (m: Prescription) => void
}

interface L1MedicationReconcilliationTableReasonProps {
	model: Prescription,
	onChange: (m: Prescription) => void,
	reconciled: boolean,
}

interface MedicationReconciliationActionsProps {
	model: Prescription,
	canReset: boolean,
	canRemove: boolean,
	onReset: () => void,
	onRemove: () => void,
	reconciled: boolean,
}

interface L1MedicationReconciliationHelpDialogProps {
	isOpen: boolean,
	onClose: () => void,
}

const HelpDialogList = styled.ul(({theme}): CSSObject => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
}))

export const L1MedicationReconciliationHelpDialog = ({ isOpen, onClose }: L1MedicationReconciliationHelpDialogProps) => {
	const instructions: string[] = [
		"Best Practice- Begin by scanning all the patient's in use drugs. You can move the camera over labels or lists to capture many drugs at once. The system will match up the drugs you scan with entries from previous claims.",
		"After that, manually enter any drugs you are unable to scan.",
		"Review the drugs listed on the table. For each drug, choose whether the patient is using it or not. You must add an Indication to any drug in use, and a Reason to any drug that is not in use.",
		"Once you've completed entering the required elements on a drug, it will sort to the bottom of the table.",
	]

	return (
		<L2Dialog onClose={onClose} show={isOpen} title="Medication Reconciliation Help">
			<L2DialogContent>
				<HelpDialogList>
					{
						instructions.map((txt, idx) => <li key={idx}>{txt}</li>)
					}
				</HelpDialogList>
			</L2DialogContent>
			<L2DialogActions showBoxShadow>
				<L1ReasonableButton 
					wide
					label="Done"
					color={FusionColor.OrangeMain}
					onClick={onClose}
				/>
			</L2DialogActions>
		</L2Dialog>
	)
}

export const MedicationReconciliationRowActions = ({model, canReset, canRemove, onReset, onRemove, reconciled}: MedicationReconciliationActionsProps) => {
	return (
		<ActionsColumn reconciled={reconciled}>
			{
				//canReset && <FontAwesomeIcon cursor='pointer' icon={faRotateLeft} onClick={onReset} />
			}
			{
				canRemove && <FontAwesomeIcon cursor='pointer' icon={faTrash} onClick={onRemove} />
			}
		</ActionsColumn>
	)
}

export const ScanOrManuallyEnterWarning = () => {
	return (
		<ScanOrManuallyEnterWarningRoot>
			<FontAwesomeIcon icon={faCircleExclamation} />
			&nbsp;Please scan or manually enter this drug to continue
		</ScanOrManuallyEnterWarningRoot>
	)
}

export const L1MedicationReconcilliationTableReason = ({model, onChange, reconciled}: L1MedicationReconcilliationTableReasonProps) => {
	const handleChange = useCallback((reason: string) => {
		model.Reason = reason || null
		// TODO [ISSUE-1314]
		// this should be in centralized redux logic for updating items
		// so that all side effects are in one known place
		// similar to Diagnosis Review
		if (!medReconItemCanAddIndication(model)) {
			model.Indications = []
		}
		onChange(model)
	}, [model, onChange])

	const reasonColoring = useCallback((model: Prescription) => {
		let color = undefined
		let background = undefined
		let borderColor = undefined
	
		if(model.InUse === false && model.Reason === null) {
			color = FusionColor.BloodOrange
			background = FusionColor.OrangeLight2
			borderColor = FusionColor.OrangeLight1
		}

		return {
			color,
			background,
			borderColor,
		}
	}, [])

	return (
		<L1MedicationReconcilliationTableReasonRoot>
			{
				medReconItemCanSelectReason(model) ? (
					<L1Select
						{...reasonColoring(model)}
						placeholder="Choose One"
						nullOption={true}
						value={model.Reason}
						onChange={(e) => handleChange(e.target.value)}
						expand
					>
						{
							Object.values(MedReconItemNotInUseReason).map(reason => (
								<option key={reason} value={reason}>{reason}</option>
							))
						}
					</L1Select>
				) : null
			}
		</L1MedicationReconcilliationTableReasonRoot>
	)
}

export const L1MedicationReconcilliationTableIndications = ({model, onAddIndication}: L1MedicationReconcilliationTableIndicationsProps) => {
	const sortedIndications = useMemo(() => {
		const unknownPrev = model.Indications.filter(x => x.Icd10Code.startsWith("FAKE")).map(x => x.Description) || []
		const indications = model.Indications.filter(x => !x.Icd10Code.startsWith("FAKE")).map(x => x.Description) || []

		unknownPrev.sort()
		indications.sort()

		return [...indications, ...unknownPrev]
	}, [model])

	const buttonColor = useMemo(() => {
		if(model.InUse === false) {
			return FusionColor.Text
		}

		return FusionColor.OrangeLight2
	}, [model])

	return (
		<L1MedicationReconcilliationTableIndicationsRoot>
			{
				model.Indications.length === 0 ? (
					<L1ReasonableButton 
						color={buttonColor}
						disabled={!medReconItemCanAddIndication(model)}
						label="Add Indication"
						icon={faPlus}
						onClick={() => onAddIndication(model)}
					/>
				) : (
					sortedIndications.map((ind, idx) => <L1MedicationReconcilliationTableIndication key={idx}>{ind}</L1MedicationReconcilliationTableIndication>)
				)
			}
		</L1MedicationReconcilliationTableIndicationsRoot>
	)
}

export const L1MedicationReconcilliationTableSource = ({model, reconciled}: L1MedicationReconcilliationTableSourceProps) => {
	let icon = <V2L1SmallRoundIcon backgroundColor={reconciled ? FusionColor.Third : undefined} />
	let secondaryIcon: ReactNode|null = null

	if(model.IsPrior) {
		icon = <V2L1SmallRoundIcon icon={faClock} backgroundColor={reconciled ? FusionColor.Third : undefined} />
	}

	switch(model.Confirmation) {
		case DrugConfirmation.Scan:
			icon = <V2L1SmallRoundIcon icon={faCamera} backgroundColor={reconciled ? FusionColor.Third : undefined} />
			if(model.IsPrior) secondaryIcon = <V2L1SmallRoundIcon size={V2L1SmallRoundIconSize.VerySmall} icon={faClock} backgroundColor={reconciled ? FusionColor.Third : undefined} />
			break
		case DrugConfirmation.ManualEntry:
			icon = <V2L1SmallRoundIcon icon={faKeyboard} backgroundColor={reconciled ? FusionColor.Third : undefined} />
			if(model.IsPrior) secondaryIcon = <V2L1SmallRoundIcon size={V2L1SmallRoundIconSize.VerySmall} icon={faClock} backgroundColor={reconciled ? FusionColor.Third : undefined} />
			break
	}

	return (
		<L1MedicationReconcilliationTableSourceRoot>
			<SourceSecondaryIconRoot>
				{ secondaryIcon }
			</SourceSecondaryIconRoot>
			{icon}
		</L1MedicationReconcilliationTableSourceRoot>
	)
}

enum InUseConfirmation {
	Yes,
	No,
}

export const L1MedicationReconcilliationTableInUse = ({model, onChange}: L1MedicationReconcilliationTableInUseProps) => {
	const handleChange = useCallback((e: InUseConfirmation) => {
		switch(e) {
			case InUseConfirmation.Yes:
				model.InUse = true
				model.Reason = null
				break
			case InUseConfirmation.No:
				model.InUse = false
				break
		}

		onChange(model)
	}, [model])

	let uncheckedColor = model.InUse === null ? FusionColor.OrangeLight2 : undefined
	return (
		<L1MedicationReconcilliationTableInUseRoot>
			<V2L1BooleanButton
				icon={faCheck}
				uncheckedColor={uncheckedColor}
				checkedColor={FusionColor.GoGreen}
				checked={model.InUse === true}
				onChange={() => handleChange(InUseConfirmation.Yes)}
			/>
			<V2L1BooleanButton
				icon={faXmark}
				uncheckedColor={uncheckedColor}
				checkedColor={FusionColor.StopRed}
				checked={model.InUse === false}
				onChange={() => handleChange(InUseConfirmation.No)}
			/>
		</L1MedicationReconcilliationTableInUseRoot>
	)
}